import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { authApi, authorizationApi, platformApi } from '../core/api/client';
import type {
  AuthOrganization,
  AuthorizationSnapshot,
  DestinationOrganization,
  LoginStep1Response,
  LauncherProduct,
  Organization,
  ProductCode,
  Profile,
} from '../core/api/types';
import { config } from '../core/config';
import { tokenStorage } from '../core/auth/tokenStorage';
import { endCustomerSso, signInFromCustomerSso } from '../core/auth/customerSso';
import { forkIfDuplicate, watchTabSession } from '../core/auth/tabSession';
import { PlatformApiError } from '../core/http/errors';
import { refreshNow, rotateWith } from '../core/http/client';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type SessionStatus = 'initializing' | 'anonymous' | 'authenticated';

export interface SessionBaseData {
  profile: Profile;
  organization: Organization;
  launcher: LauncherProduct[];
  snapshot: AuthorizationSnapshot;
}

export interface SessionState extends SessionBaseData {
  status: SessionStatus;
  productCode: ProductCode;
  activeOrganizationId: string | null;
  /** Organizations returned by login step 1 — used for the org switcher. */
  orgChoices: AuthOrganization[];
  authError: string | null;
}

const initialState: SessionState = {
  status: 'initializing',
  profile: {
    id: '',
    email: '',
    userName: '',
    firstName: '',
    lastName: '',
    phone: null,
    avatarObjectKey: null,
    emailVerifiedAt: null,
  },
  organization: {
    id: '',
    code: '',
    name: '',
    registrationNumber: null,
    email: null,
    phone: null,
    address: null,
    timeZone: 'UTC',
    currencyCode: 'EUR',
    // Legacy Company parity (PlatformDB script 017) — see `core/api/types.ts`.
    location: null,
    description: null,
    website: null,
    languageCode: null,
    fax: null,
  },
  launcher: [],
  snapshot: { productCode: 'PLATFORM', resources: [] },
  productCode: config.productCode,
  activeOrganizationId: null,
  orgChoices: [],
  authError: null,
};

// ---------------------------------------------------------------------------
// Plain actions
// ---------------------------------------------------------------------------

const slice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setInitializing(state) {
      state.status = 'initializing';
      state.authError = null;
    },
    setAnonymous(state) {
      state.status = 'anonymous';
      state.authError = null;
    },
    setOrgChoices(state, action: PayloadAction<AuthOrganization[]>) {
      state.orgChoices = action.payload;
    },
    setAuthError(state, action: PayloadAction<string>) {
      state.authError = action.payload;
    },
    ready(state, action: PayloadAction<SessionBaseData>) {
      state.status = 'authenticated';
      state.authError = null;
      state.profile = action.payload.profile;
      state.organization = action.payload.organization;
      state.launcher = action.payload.launcher;
      state.snapshot = action.payload.snapshot;
      state.activeOrganizationId = action.payload.organization.id;
    },
    sessionExpired(state) {
      state.status = 'anonymous';
      state.profile = initialState.profile;
      state.organization = initialState.organization;
      state.launcher = [];
      state.snapshot = initialState.snapshot;
      state.activeOrganizationId = null;
      state.orgChoices = [];
      state.authError = 'Your session has expired. Please sign in again.';
    },
  },
});

export const { setInitializing, setAnonymous, setOrgChoices, setAuthError, ready, sessionExpired } =
  slice.actions;

// ---------------------------------------------------------------------------
// Shared helper used after every token acquisition
// ---------------------------------------------------------------------------

async function loadBaseData(): Promise<SessionBaseData> {
  const [profile, organization, launcher, snapshot] = await Promise.all([
    platformApi.profile(),
    platformApi.organization(),
    platformApi.launcher(),
    authorizationApi.snapshot(),
  ]);
  return { profile, organization, launcher, snapshot };
}

function toApiError(error: unknown, fallback: string): PlatformApiError {
  return error instanceof PlatformApiError ? error : PlatformApiError.fromAxios(error, fallback);
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

let booted: Promise<void> = Promise.resolve();

/**
 * The business organization the user last chose in this browser (not secret; the API re-checks it).
 * A tab restored from the customer SSO session starts in COMMUNITY and moves back here.
 */
const LAST_ORG_KEY = 'architech.platform.v1.lastOrganizationId';

function rememberOrganization(organizationId: string | null): void {
  try {
    if (organizationId) window.localStorage.setItem(LAST_ORG_KEY, organizationId);
    else window.localStorage.removeItem(LAST_ORG_KEY);
  } catch { /* storage blocked */ }
}

async function returnToLastOrganization(): Promise<void> {
  let last: string | null = null;
  try { last = window.localStorage.getItem(LAST_ORG_KEY); } catch { return; }
  const context = tokenStorage.getRefreshContext();
  if (!last || !context || context.organizationId === last) return;
  try {
    await rotateWith(last, context.productCode);
  } catch (error) {
    // No longer a member (403): stay in COMMUNITY and stop trying. Anything else: try next time.
    if (PlatformApiError.fromAxios(error, '').status === 403) rememberOrganization(null);
  }
}

/**
 * Resolves once boot has restored, adopted or given up on this tab's session; never while the
 * browser is leaving for a silent SSO check. Wait for it before navigating away on load, or the
 * check (or a refresh token just rotated) is lost with the page.
 */
export function whenSessionBooted(): Promise<void> {
  return booted;
}

/**
 * Boot/restore: if a refresh context exists (previous session in this tab),
 * rotate once to obtain an access token and reload base data. A tab without
 * one takes over the browser's customer SSO session when there is one (see
 * customerSso.ts). Otherwise the app lands on the anonymous state and
 * redirects to /login.
 */
export const bootstrapSession = createAsyncThunk(
  'session/bootstrap',
  async (_: void, { dispatch }): Promise<{ restored: boolean }> => {
    let settle!: () => void;
    booted = new Promise<void>((resolve) => { settle = resolve; });
    let leaving = false;
    try {
      dispatch(setInitializing());
      // A duplicated tab carries a copy of another tab's refresh token; get one of its own first.
      await forkIfDuplicate().catch(() => tokenStorage.clear());
      if (tokenStorage.getRefreshContext()) {
        try {
          if (!tokenStorage.getAccessToken()) await refreshNow();
          const data = await loadBaseData();
          dispatch(setOrgChoices(tokenStorage.getOrgChoices()));
          dispatch(ready(data));
          return { restored: true };
        } catch {
          tokenStorage.clear();
        }
      }
      const sso = await signInFromCustomerSso().catch(() => 'none' as const);
      // Still 'initializing' while the browser leaves for the silent check.
      leaving = sso === 'redirecting';
      if (leaving) return { restored: false };
      if (sso === 'adopted') {
        try {
          await returnToLastOrganization();
          dispatch(ready(await loadBaseData()));
          return { restored: true };
        } catch {
          tokenStorage.clear();
        }
      }
      dispatch(setAnonymous());
      return { restored: false };
    } finally {
      if (!leaving) { watchTabSession(); settle(); }
    }
  },
);

/**
 * Login step 1: authenticate and discover the user's organizations × destinations.
 *
 * An API older than the Hub destination popup returns organizations without `destinations`; each
 * is then offered as a single Configuration card, which is exactly what that API could open.
 */
export const discoverOrganizations = createAsyncThunk(
  'session/discoverOrganizations',
  async (credentials: { login: string; password: string }, { dispatch }): Promise<LoginStep1Response> => {
    try {
      const raw = await authApi.loginStep1(credentials.login, credentials.password);
      const organizations: DestinationOrganization[] = (raw.organizations ?? []).map((organization) => ({
        ...organization,
        displayName: organization.displayName ?? organization.name,
        destinations: organization.destinations
          ?? [{ code: 'PLATFORM', kind: 'configuration', displayName: 'Configuration', icon: 'Settings' }],
      }));
      const result: LoginStep1Response = { ...raw, organizations };
      dispatch(setOrgChoices(organizations));
      tokenStorage.setOrgChoices(organizations);
      return result;
    } catch (error) {
      const mapped = toApiError(error, 'Sign in failed.');
      dispatch(setAuthError(mapped.message));
      throw mapped;
    }
  },
);

/** Login step 2: select organization + product, obtain tokens, load base data. */
export const completeLogin = createAsyncThunk(
  'session/completeLogin',
  async (
    arg: { login: string; password: string; organizationId: string; productCode: ProductCode },
    { dispatch },
  ): Promise<SessionBaseData> => {
    try {
      const pair = await authApi.loginStep2(arg.login, arg.password, arg.organizationId, arg.productCode);
      tokenStorage.applyTokenPair(pair, { organizationId: arg.organizationId, productCode: arg.productCode });
      rememberOrganization(arg.organizationId);
      const data = await loadBaseData();
      dispatch(ready(data));
      return data;
    } catch (error) {
      const mapped = toApiError(error, 'Sign in failed.');
      dispatch(setAuthError(mapped.message));
      throw mapped;
    }
  },
);

/** Switch active organization (and product) by rotating the session. */
export const switchOrganization = createAsyncThunk(
  'session/switchOrganization',
  async (arg: { organizationId: string; productCode: ProductCode }, { dispatch }): Promise<SessionBaseData> => {
    try {
      await rotateWith(arg.organizationId, arg.productCode);
      rememberOrganization(arg.organizationId);
      const data = await loadBaseData();
      dispatch(ready(data));
      return data;
    } catch (error) {
      const mapped = toApiError(error, 'Switching organization failed.');
      dispatch(setAuthError(mapped.message));
      throw mapped;
    }
  },
);

/** End the session: revoke the refresh token server-side and clear all state. */
export const signOut = createAsyncThunk('session/signOut', async (_: void, { dispatch }): Promise<void> => {
  const context = tokenStorage.getRefreshContext();
  if (context) {
    try {
      await authApi.logout(context.refreshToken);
    } catch {
      // Logout is best-effort; local state must still be cleared.
    }
    try {
      await endCustomerSso();
    } catch {
      // Best-effort as well.
    }
  }
  tokenStorage.clear();
  rememberOrganization(null);
  dispatch(setAnonymous());
});

/** Re-fetch the tenant-scoped base data after a mutation (profile, organization). */
export const reloadSessionData = createAsyncThunk(
  'session/reload',
  async (_: void, { dispatch }): Promise<SessionBaseData> => {
    const data = await loadBaseData();
    dispatch(ready(data));
    return data;
  },
);

export default slice.reducer;

/** Complete a server-verified Google sign-in with the same session setup as password login. */
export const completeGoogleLogin = createAsyncThunk('session/google', async (
  arg: { credential: string; organizationId: string }, { dispatch },
): Promise<SessionBaseData> => {
  const { http } = await import('../core/http/client');
  const { data: pair } = await http().post<import('../core/api/types').TokenPair>('/auth/google', arg, { skipAuthRefresh: true });
  tokenStorage.applyTokenPair(pair, { organizationId: arg.organizationId, productCode: 'PLATFORM' });
  const data = await loadBaseData();
  dispatch(ready(data));
  return data;
});
